import { Coins, Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { useMemo } from "react";

import { cn } from "@/lib/utils";
import useTranslations from "@/shared/lang/useTranslations";

import { CURRENCY_OPTIONS } from "../../confirmations/constants/currencies.config";
import type { OperationCurrency, PendingCurrencyTotal } from "../types/payments-api.types";
import { formatPaymentAmount } from "../utils/formatPaymentAmount";

const CURRENCY_ORDER: ReadonlyMap<string, number> = new Map(
  CURRENCY_OPTIONS.map((opt, index) => [opt.value, index]),
);

type AccentVariant = "fiat" | "crypto" | "neutral";

const CRYPTO_CURRENCIES: ReadonlySet<OperationCurrency> = new Set(["USDT", "USDC", "BINANCE_PAY"]);

function getAccentVariant(currency: OperationCurrency): AccentVariant {
  if (CRYPTO_CURRENCIES.has(currency)) return "crypto";
  if (currency === "USD" || currency === "EUR" || currency === "HKD" || currency === "CNY") {
    return "neutral";
  }
  return "fiat";
}

const accentClasses: Record<
  AccentVariant,
  { ring: string; chip: string; glow: string; iconBg: string; iconText: string }
> = {
  fiat: {
    ring: "before:from-blue-500/70 before:via-blue-400/60 before:to-cyan-400/60",
    chip: "bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/30",
    glow: "from-blue-500/10 via-transparent to-transparent",
    iconBg: "bg-blue-100 dark:bg-blue-500/15",
    iconText: "text-blue-600 dark:text-blue-300",
  },
  crypto: {
    ring: "before:from-amber-500/70 before:via-orange-400/60 before:to-yellow-400/60",
    chip: "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30",
    glow: "from-amber-500/10 via-transparent to-transparent",
    iconBg: "bg-amber-100 dark:bg-amber-500/15",
    iconText: "text-amber-600 dark:text-amber-300",
  },
  neutral: {
    ring: "before:from-emerald-500/70 before:via-teal-400/60 before:to-green-400/60",
    chip: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30",
    glow: "from-emerald-500/10 via-transparent to-transparent",
    iconBg: "bg-emerald-100 dark:bg-emerald-500/15",
    iconText: "text-emerald-600 dark:text-emerald-300",
  },
};

type PendingTotalsSummaryProps = {
  totals: PendingCurrencyTotal[];
  isLoading: boolean;
  errorMessage: string | null;
  onRefresh: () => void;
};

export const PendingTotalsSummary = ({
  totals,
  isLoading,
  errorMessage,
  onRefresh,
}: PendingTotalsSummaryProps) => {
  const { t } = useTranslations("payments");

  const visibleTotals = useMemo(() => {
    return totals
      .filter((item) => CURRENCY_ORDER.has(item.currency) && item.total > 0)
      .sort((a, b) => {
        const ia = CURRENCY_ORDER.get(a.currency) ?? Number.MAX_SAFE_INTEGER;
        const ib = CURRENCY_ORDER.get(b.currency) ?? Number.MAX_SAFE_INTEGER;
        return ia - ib;
      });
  }, [totals]);

  return (
    <section
      aria-labelledby="pending-totals-heading"
      className="rounded-2xl border border-gray-200 dark:border-zinc-800 bg-gradient-to-br from-white via-white to-slate-50 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-950 p-5 sm:p-6 shadow-sm"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 text-white shadow-md shadow-blue-500/20 shrink-0">
            <Coins className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2
              id="pending-totals-heading"
              className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white"
            >
              {t("pending_totals_title")}
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-zinc-400">
              {t("pending_totals_subtitle")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isLoading}
          className="self-start sm:self-auto inline-flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-zinc-300 hover:text-blue-600 dark:hover:text-blue-300 transition-colors disabled:opacity-50"
          aria-label={t("pending_totals_refresh_aria")}
          title={t("pending_totals_refresh_aria")}
        >
          <RefreshCw className={cn("h-3.5 w-3.5", isLoading && "animate-spin")} aria-hidden />
          {t("pending_totals_refresh")}
        </button>
      </div>

      {isLoading && visibleTotals.length === 0 ? (
        <div className="flex items-center justify-center py-10 text-gray-500 dark:text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden />
          <span className="text-sm">{t("pending_totals_loading")}</span>
        </div>
      ) : errorMessage ? (
        <div className="flex items-center gap-2 py-6 text-sm text-rose-600 dark:text-rose-400">
          <AlertCircle className="h-4 w-4" aria-hidden />
          {errorMessage}
        </div>
      ) : visibleTotals.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-sm text-gray-500 dark:text-zinc-400 italic">
          {t("pending_totals_empty")}
        </div>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {visibleTotals.map((item) => {
            const variant = getAccentVariant(item.currency);
            const accent = accentClasses[variant];
            return (
              <li
                key={item.currency}
                className={cn(
                  "group relative overflow-hidden rounded-xl bg-white dark:bg-zinc-900/80 border border-gray-200 dark:border-zinc-800 p-4 shadow-sm hover:shadow-md transition-all",
                  "before:content-[''] before:absolute before:inset-x-0 before:top-0 before:h-1 before:bg-gradient-to-r",
                  accent.ring,
                )}
              >
                <div
                  className={cn(
                    "pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full bg-gradient-to-br blur-2xl opacity-70 group-hover:opacity-100 transition-opacity",
                    accent.glow,
                  )}
                  aria-hidden
                />
                <div className="relative flex items-center justify-between gap-2 mb-3">
                  <span
                    className={cn(
                      "inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wider",
                      accent.chip,
                    )}
                  >
                    {item.currency}
                  </span>
                  <span
                    className={cn(
                      "flex items-center justify-center h-7 w-7 rounded-lg",
                      accent.iconBg,
                      accent.iconText,
                    )}
                    aria-hidden
                  >
                    <Coins className="h-3.5 w-3.5" />
                  </span>
                </div>
                <p className="relative text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-zinc-500 mb-1">
                  {t("pending_totals_card_label")}
                </p>
                <p
                  className="relative text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tabular-nums tracking-tight break-all transition-[color,transform] duration-200"
                  title={`${item.currency} ${formatPaymentAmount(item.total)}`}
                >
                  {formatPaymentAmount(item.total)}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
